/**
 * Plain text that carries HTML tags, as when Overview copy comes out of an HTML or code view or out of
 * BigMarker, arrives in a paste as `text/plain`. The editor stores Markdown, so the tags were kept as
 * literal characters (ORB-939). This turns the common ones into Markdown and drops the rest.
 *
 * A rich paste (Google Docs, a web page) arrives as `text/html` and never reaches this; the editor
 * already converts those.
 */

/**
 * The tags a paste is converted for. A known list rather than "anything in angle brackets", so text
 * like `if x<y and z>3` or a `<First Name>` placeholder is never mistaken for HTML and deleted.
 * script and style are here only so their content is dropped instead of pasted as text.
 */
const KNOWN_TAG_NAMES = 'b|strong|i|em|u|a|br|p|div|span|ul|ol|li|h[1-6]|script|style'

/**
 * Attributes that are valid with no value on the tags above. Listed, not "any bare word": `<b and c>`
 * is also a name followed by bare words, and it has to stay text.
 */
const BOOLEAN_ATTRIBUTES = 'async|defer|nomodule|reversed|download|hidden|inert|itemscope|autofocus'

/**
 * What follows `<` in a real tag: a closing tag, or a name with only `name=value` attributes (the value
 * quoted or not, since minified HTML drops the quotes) or known valueless ones. A name alone isn't
 * enough, since `if a<b and c>d` starts with one; its bare words fail both attribute rules.
 */
const ATTRIBUTE = String.raw`\s+(?:[\w:-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>\x60]+)|(?:${BOOLEAN_ATTRIBUTES})(?=[\s/>]))`
const TAG_BODY = String.raw`(?:\/(?:${KNOWN_TAG_NAMES})\s*|(?:${KNOWN_TAG_NAMES})(?:${ATTRIBUTE})*\s*\/?)>`
const FORMATTING_TAG = new RegExp(`<${TAG_BODY}`, 'i')

/** A `<` that doesn't open a known tag: escaped before parsing, so it stays in the text as written. */
const OTHER_ANGLE_BRACKET = new RegExp(`<(?!${TAG_BODY})`, 'gi')

/** Line-start bullet characters from Word, PDFs and BigMarker, which the Overview has no meaning for. */
const BULLET_LINE = /^[ \t]*[●•▪◦][ \t]*/gm

/**
 * A list line followed straight away by a line that is not a list item, a blank or the end. Markdown reads
 * that line as more of the last item, so pasted copy with a paragraph right after its bullets ran the
 * paragraph into the last bullet (ORB-939, stage test).
 */
const LIST_LINE_THEN_TEXT = /^([ \t]*(?:[-*+]|\d+[.)])[ \t].*)\n(?![ \t]*(?:[-*+]|\d+[.)])[ \t]|[ \t]*$)/gm

/** Entities like &nbsp; decode to this, which would otherwise survive as an invisible non-space. */
const NO_BREAK_SPACE = String.fromCharCode(0xa0)

/** Whether this pasted text is worth converting: it contains a tag we know, or a bullet character. */
export function hasPastedFormatting(text: string): boolean {
    BULLET_LINE.lastIndex = 0
    return FORMATTING_TAG.test(text) || BULLET_LINE.test(text)
}

/**
 * Markdown for pasted plain text containing HTML tags or bullet characters, or null when there is
 * nothing to convert so the caller can leave the paste alone. Line breaks already in the text are
 * kept: in a plain-text paste they are the author's, even though HTML would collapse them.
 */
export function pastedHtmlToMarkdown(pasted: string): string | null {
    if (!hasPastedFormatting(pasted)) return null
    // Windows clipboard text can end lines with CRLF, and every line rule below matches LF only.
    const text = pasted.replace(/\r\n?/g, '\n')

    let markdown = text
    if (FORMATTING_TAG.test(text)) {
        const doc = new DOMParser().parseFromString(`<body>${text.replace(OTHER_ANGLE_BRACKET, '&lt;')}</body>`, 'text/html')
        markdown = convertChildren(doc.body, { listDepth: 0 })
    }

    BULLET_LINE.lastIndex = 0
    const body = markdown
        .replace(BULLET_LINE, '- ')
        .replace(LIST_LINE_THEN_TEXT, '$1\n\n')
        .split(NO_BREAK_SPACE).join(' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    // Blank lines at either end of the paste are the author's, and separate it from what's around the
    // cursor; trimming them ran a pasted paragraph onto the end of the text before it.
    return edgeNewlines(text, /^\s*/) + body + edgeNewlines(text, /\s*$/)
}

function edgeNewlines(text: string, edge: RegExp): string {
    const newlines = (edge.exec(text)?.[0].match(/\n/g) ?? []).length
    return '\n'.repeat(Math.min(newlines, 2))
}

/**
 * A paste into a plain Markdown textarea: the converted text in place of the selection, and where the
 * cursor goes after it. Null when there is nothing to convert, so the browser's own paste runs.
 */
export function pasteIntoMarkdown(current: string, selectionStart: number, selectionEnd: number, pasted: string): { value: string, cursor: number } | null {
    const converted = pastedHtmlToMarkdown(pasted)
    if (converted === null) return null
    return {
        value: current.slice(0, selectionStart) + converted + current.slice(selectionEnd),
        cursor: selectionStart + converted.length,
    }
}

type Context = { listDepth: number, ordered?: boolean }

function convertChildren(node: Node, context: Context): string {
    let out = ''
    let index = 0
    node.childNodes.forEach(child => {
        if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName.toLowerCase() === 'li') index += 1
        out += convertNode(child, context, index)
    })
    return out
}

function convertNode(node: Node, context: Context, index: number): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
    if (node.nodeType !== Node.ELEMENT_NODE) return ''

    const element = node as Element
    const inner = () => convertChildren(element, context)

    switch (element.tagName.toLowerCase()) {
        case 'b':
        case 'strong':
            return wrap(inner(), '**')
        case 'i':
        case 'em':
            return wrap(inner(), '*')
        case 'a': {
            const href = element.getAttribute('href')?.trim()
            const label = inner()
            return href && /^(https?:|mailto:)/i.test(href) ? `[${label.trim() || href}](${href})` : label
        }
        case 'br':
            return '\n'
        case 'script':
        case 'style':
            return ''
        case 'p':
        case 'div':
            return `\n\n${inner().trim()}\n\n`
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6': {
            const level = Math.min(Number(element.tagName[1]), 3)
            return `\n\n${'#'.repeat(level)} ${inner().trim()}\n\n`
        }
        case 'ul':
        case 'ol': {
            const nested = { listDepth: context.listDepth + 1, ordered: element.tagName.toLowerCase() === 'ol' }
            return `\n${convertChildren(element, nested)}\n`
        }
        case 'li': {
            const indent = '  '.repeat(Math.max(context.listDepth - 1, 0))
            const marker = context.ordered ? `${index}.` : '-'
            return `${indent}${marker} ${inner().trim()}\n`
        }
        // Underline has no Markdown form and the editor drops it on save anyway; unknown tags keep their text.
        default:
            return inner()
    }
}

/** Markdown emphasis only binds when the markers touch the text, so move edge spaces outside them. */
function wrap(text: string, marker: string): string {
    const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text)
    const [, lead, body, trail] = match ?? ['', '', text, '']
    return body ? `${lead}${marker}${body}${marker}${trail}` : text
}
